import * as React from "react";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
} from "lucide-react";
import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  format,
  isAfter,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from "date-fns";
import { ar as arLocale } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/utils/cn";
import { normalizeLanguage } from "@/utils/language";

type Props = {
  startDate: Date;
  endDate: Date;
  onChange: (start: Date, end: Date) => void;
};

type Preset = {
  id: string;
  labelKey: string;
  compute: () => [Date, Date];
};

const now = () => new Date();

const PRESETS: Preset[] = [
  {
    id: "today",
    labelKey: "usageAndCost.dateRange.today",
    compute: () => [startOfDay(now()), endOfDay(now())],
  },
  {
    id: "7d",
    labelKey: "usageAndCost.dateRange.last7Days",
    compute: () => [startOfDay(subDays(now(), 6)), endOfDay(now())],
  },
  {
    id: "14d",
    labelKey: "usageAndCost.dateRange.last2Weeks",
    compute: () => [startOfDay(subDays(now(), 13)), endOfDay(now())],
  },
  {
    id: "30d",
    labelKey: "usageAndCost.dateRange.last30Days",
    compute: () => [startOfDay(subDays(now(), 29)), endOfDay(now())],
  },
  {
    id: "this-month",
    labelKey: "usageAndCost.dateRange.thisMonth",
    compute: () => [startOfMonth(now()), endOfDay(now())],
  },
  {
    id: "past-month",
    labelKey: "usageAndCost.dateRange.pastMonth",
    compute: () => {
      const d = subMonths(now(), 1);
      return [startOfMonth(d), endOfMonth(d)];
    },
  },
  {
    id: "past-2-months",
    labelKey: "usageAndCost.dateRange.past2Months",
    compute: () => {
      const start = startOfMonth(subMonths(now(), 2));
      const end = endOfMonth(subMonths(now(), 1));
      return [start, end];
    },
  },
  {
    id: "last-3-months",
    labelKey: "usageAndCost.dateRange.last3Months",
    compute: () => [startOfDay(subMonths(now(), 3)), endOfDay(now())],
  },
  {
    id: "this-year",
    labelKey: "usageAndCost.dateRange.thisYear",
    compute: () => [startOfYear(now()), endOfDay(now())],
  },
  {
    id: "1y",
    labelKey: "usageAndCost.dateRange.lastYear",
    compute: () => [startOfDay(subYears(now(), 1)), endOfDay(now())],
  },
];

const WEEKDAY_KEYS = ["su", "mo", "tu", "we", "th", "fr", "sa"] as const;

const isValidDate = (d: unknown): d is Date =>
  d instanceof Date && !Number.isNaN(d.getTime());

const clamp = (n: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, n));

// Fall back to a valid date so downstream date-fns calls never throw.
const safeDate = (d: Date): Date => (isValidDate(d) ? d : new Date());

const toTimeInput = (d: Date) =>
  isValidDate(d) ? format(d, "HH:mm:ss") : "00:00:00";

const applyTime = (date: Date, timeStr: string): Date => {
  const parts = (timeStr ?? "").split(":");
  const h = clamp(parseInt(parts[0], 10) || 0, 0, 23);
  const m = clamp(parseInt(parts[1], 10) || 0, 0, 59);
  const s = clamp(parseInt(parts[2], 10) || 0, 0, 59);
  const next = new Date(safeDate(date));
  next.setHours(h, m, s, 0);
  return next;
};

function buildCalendarGrid(month: Date): Date[] {
  const firstOfMonth = startOfMonth(safeDate(month));
  const gridStart = startOfWeek(firstOfMonth, { weekStartsOn: 0 });
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) days.push(addDays(gridStart, i));
  return days;
}

export function DateRangePicker({ startDate, endDate, onChange }: Props) {
  const { t } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const dateLocale =
    normalizeLanguage(i18n.language) === "ar" ? arLocale : undefined;
  const formatTrigger = (d: Date) =>
    isValidDate(d)
      ? `${format(d, "d MMM", { locale: dateLocale })}, ${format(d, "hh:mm:ss a", { locale: dateLocale })}`
      : "—";

  const [open, setOpen] = React.useState(false);
  const [localStart, setLocalStart] = React.useState<Date>(startDate);
  const [localEnd, setLocalEnd] = React.useState<Date>(endDate);
  const [anchor, setAnchor] = React.useState<"start" | "end">("start");
  const [hoveredDay, setHoveredDay] = React.useState<Date | null>(null);
  const [viewMonth, setViewMonth] = React.useState<Date>(
    startOfMonth(safeDate(startDate)),
  );

  React.useEffect(() => {
    if (open) {
      setLocalStart(safeDate(startDate));
      setLocalEnd(safeDate(endDate));
      setAnchor("start");
      setHoveredDay(null);
      setViewMonth(startOfMonth(safeDate(startDate)));
    }
    // Only re-sync on open transitions; prop changes from our own commits
    // would otherwise reset the anchor mid-selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const commit = (s: Date, e: Date) => {
    onChange(s, e);
  };

  // Skyscanner-style two-step selection:
  //   1. First click sets the start and collapses end to the same day,
  //      moving into "pick end" mode. Hovering then previews the range.
  //   2. Second click on or after start commits the end and returns to
  //      "pick start" mode (next click starts a fresh range).
  //   3. Second click BEFORE start replaces the start; we stay in
  //      "pick end" mode so the user can complete the range.
  const handleDayClick = (day: Date) => {
    if (anchor === "start") {
      const nextStart = applyTime(day, toTimeInput(localStart));
      const nextEnd = applyTime(day, toTimeInput(localEnd));
      setLocalStart(nextStart);
      setLocalEnd(nextEnd);
      commit(nextStart, nextEnd);
      setAnchor("end");
      return;
    }

    // anchor === "end"
    const startDay = startOfDay(localStart);
    if (startOfDay(day).getTime() < startDay.getTime()) {
      // Clicked before the start — treat as a new start, keep waiting for end.
      const nextStart = applyTime(day, toTimeInput(localStart));
      const nextEnd = applyTime(day, toTimeInput(localEnd));
      setLocalStart(nextStart);
      setLocalEnd(nextEnd);
      commit(nextStart, nextEnd);
      return;
    }

    const nextEnd = applyTime(day, toTimeInput(localEnd));
    setLocalEnd(nextEnd);
    commit(localStart, nextEnd);
    setAnchor("start");
    setHoveredDay(null);
  };

  const handleStartTime = (value: string) => {
    const next = applyTime(localStart, value);
    setLocalStart(next);
    commit(next, localEnd);
  };

  const handleEndTime = (value: string) => {
    const next = applyTime(localEnd, value);
    setLocalEnd(next);
    commit(localStart, next);
  };

  const handlePreset = (p: Preset) => {
    const [s, e] = p.compute();
    setLocalStart(s);
    setLocalEnd(e);
    setViewMonth(startOfMonth(s));
    setAnchor("start");
    commit(s, e);
  };

  const days = buildCalendarGrid(viewMonth);
  const weekdayLabels = WEEKDAY_KEYS.map((key) =>
    t(`usageAndCost.dateRange.weekdays.${key}`),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-border bg-input px-3 text-sm text-foreground outline-hidden transition-colors hover:bg-input/80">
          <span className="inline-flex min-w-0 items-center gap-2">
            <Calendar className="size-4 text-muted-foreground" />
            <span className="truncate text-foreground">
              {formatTrigger(startDate)} - {formatTrigger(endDate)}
            </span>
          </span>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[640px] border-border bg-popover p-0"
      >
        <div className="grid grid-cols-[1fr_1.1fr] gap-0">
          <div className="flex flex-col gap-4 border-e border-border p-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-muted-foreground">
                  {t("usageAndCost.dateRange.startTime")}
                </label>
                <input
                  type="time"
                  step={1}
                  value={toTimeInput(localStart)}
                  onChange={(e) => handleStartTime(e.target.value)}
                  className="h-9 rounded-md border border-border bg-input px-2.5 text-sm text-foreground outline-hidden focus:border-primary"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-muted-foreground">
                  {t("usageAndCost.dateRange.endTime")}
                </label>
                <input
                  type="time"
                  step={1}
                  value={toTimeInput(localEnd)}
                  onChange={(e) => handleEndTime(e.target.value)}
                  className="h-9 rounded-md border border-border bg-input px-2.5 text-sm text-foreground outline-hidden focus:border-primary"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="px-1 pb-1 text-xs text-muted-foreground">
                {t("usageAndCost.dateRange.presetRanges")}
              </div>
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handlePreset(p)}
                  className="rounded-md px-2.5 py-1.5 text-start text-sm text-foreground transition-colors hover:bg-muted"
                >
                  {t(p.labelKey)}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 p-4">
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                {t("usageAndCost.dateRange.date")}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMonth(subMonths(viewMonth, 1))}
                  className="flex size-8 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label={t("usageAndCost.dateRange.previousMonth")}
                >
                  {isRTL ? (
                    <ChevronRight className="size-4" />
                  ) : (
                    <ChevronLeft className="size-4" />
                  )}
                </button>
                <div className="min-w-[120px] text-center text-sm font-medium text-foreground">
                  {format(viewMonth, "MMMM yyyy", { locale: dateLocale })}
                </div>
                <button
                  onClick={() => setViewMonth(addMonths(viewMonth, 1))}
                  className="flex size-8 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label={t("usageAndCost.dateRange.nextMonth")}
                >
                  {isRTL ? (
                    <ChevronLeft className="size-4" />
                  ) : (
                    <ChevronRight className="size-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-y-1">
              {weekdayLabels.map((w, i) => (
                <div
                  key={`${w}-${i}`}
                  className="py-1 text-center text-xs font-medium text-muted-foreground"
                >
                  {w}
                </div>
              ))}
              {(() => {
                // Compute the visible end for rendering. While in "pick end"
                // mode, the hovered day previews the range (if it's not
                // before the start). With no hover, only the start dot shows.
                const startDay = startOfDay(localStart);
                const committedEndDay = startOfDay(localEnd);
                const hoverDay = hoveredDay ? startOfDay(hoveredDay) : null;
                const previewEndDay =
                  anchor === "end" &&
                  hoverDay &&
                  hoverDay.getTime() >= startDay.getTime()
                    ? hoverDay
                    : anchor === "end"
                      ? startDay
                      : committedEndDay;

                return days.map((day, idx) => {
                  const inMonth = isSameMonth(day, viewMonth);
                  const dayStart = startOfDay(day);
                  const isStart = isSameDay(day, startDay);
                  const isEnd = isSameDay(day, previewEndDay);
                  const isInRange =
                    dayStart.getTime() >= startDay.getTime() &&
                    dayStart.getTime() <= previewEndDay.getTime();
                  const isEdge = isStart || isEnd;
                  const isPreview = anchor === "end" && !!hoverDay;

                  return (
                    <button
                      key={idx}
                      onClick={() => handleDayClick(day)}
                      onMouseEnter={() =>
                        anchor === "end" ? setHoveredDay(day) : undefined
                      }
                      onMouseLeave={() =>
                        anchor === "end" ? setHoveredDay(null) : undefined
                      }
                      className={cn(
                        "relative flex h-9 items-center justify-center text-sm transition-colors",
                        !inMonth && "text-muted-foreground/50",
                        inMonth &&
                          !isInRange &&
                          "text-foreground hover:bg-muted",
                        isInRange &&
                          !isEdge &&
                          (isPreview
                            ? "bg-emerald-900/25 text-foreground"
                            : "bg-emerald-900/40 text-foreground"),
                        isEdge && "bg-emerald-600 font-medium text-white",
                        isStart && !isEnd && "rounded-s-md",
                        isEnd && !isStart && "rounded-e-md",
                        isStart && isEnd && "rounded-md",
                      )}
                    >
                      {format(day, "d")}
                    </button>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
